import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Box, CircularProgress, Grid, LinearProgress, MenuItem, TextField, Tooltip } from '@material-ui/core';
import { EmojiEvents, ErrorOutline, Flag, Public, Timer, VerifiedUser, Whatshot } from '@material-ui/icons';
import clsx from 'clsx';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { getPveInstance, pveInstanceHasHardMode } from '@pvplevelup/core/dist/interfaces/Pve/IPveInstance';
import {
  PveClearLeaderboardQuery, PveClearLeaderboardData, PveClearLeaderboardVars, PveKillLeaderboardQuery, PveKillLeaderboardData,
  PveKillLeaderboardVars, IPveLeaderboardPlayer,
} from '@pvplevelup/core/dist/queries/pve/pveLeaderboard';
import { ClassCounts } from '../run/ClassCounts';
import { PveIcons } from 'utilities/pve_icons';
import { getPveKind, getReadyPveInstances, Segmented } from '../run/PveRunList';
import { formatPveDuration } from '../run/pveFormat';
import { PveNav } from '../PveNav';
import { EncounterPicker, EncounterPickerButton } from '../EncounterPicker';
import { PageEmpty, PAGE_KIT, usePageStyles } from 'components/Page/PageKit';
import { LeaderboardSeasonsData, LeaderboardSeasonsQuery } from '@pvplevelup/core/dist/queries/leaderboard/seasons';
import { ISeason } from '@pvplevelup/core/dist/interfaces/ISeason';

/** a season's name and dates, as the PvP leaderboards show it */
const seasonLabel = (s: ISeason) => `${s.label} (${moment(s.start, "YYYY-MM-DD").format("MMM Do YYYY")} - ${moment(s.end, "YYYY-MM-DD").format("MMM Do YYYY")})`;

const HARD = '#ff7043';
/** gold, silver and bronze for the top 3 */
const MEDALS = [
  {color: '#ffc107', badge: 'linear-gradient(135deg, #ffe082, #ffb300)', text: '#3e2700'},
  {color: '#b0bec5', badge: 'linear-gradient(135deg, #eceff1, #90a4ae)', text: '#263238'},
  {color: '#d7884a', badge: 'linear-gradient(135deg, #ffcc80, #b0652c)', text: '#3e1f00'},
];

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins
const useStyles = makeStyles((theme) => ({
  filterPanel: {
    marginBottom: theme.spacing(2),
  },
  // wrapping rows of 40px controls; outlined labels float above, so nothing here clips
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: -theme.spacing(1.25),
    '& > *': {
      marginRight: theme.spacing(1.5),
      marginBottom: theme.spacing(1.25),
    },
  },
  controlsDivider: {
    marginTop: theme.spacing(1.25),
    paddingTop: theme.spacing(2),
    borderTop: PAGE_KIT.divider,
  },
  select: {
    '& .MuiOutlinedInput-root': {
      height: PAGE_KIT.controlHeight,
      borderRadius: 10,
      background: 'rgba(0, 0, 0, 0.3)',
    },
  },
  menuIcon: {
    width: 18,
    height: 18,
    marginRight: 8,
    verticalAlign: 'middle',
  },
  note: {
    marginTop: theme.spacing(1.5),
    fontSize: '0.82rem',
    color: PAGE_KIT.muted,
  },

  // the results
  banner: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(2, 2.25),
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    borderBottom: PAGE_KIT.divider,
  },
  bannerIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    display: 'grid',
    placeItems: 'center',
    color: MEDALS[0].color,
    background: 'rgba(255, 193, 7, 0.14)',
    border: '1px solid rgba(255, 193, 7, 0.35)',
    '& svg': {
      fontSize: 24,
    },
  },
  bannerTitle: {
    fontSize: '1.2rem',
    fontWeight: 800,
    lineHeight: 1.2,
    textShadow: '0 1px 4px rgba(0, 0, 0, 0.6)',
  },
  bannerMeta: {
    display: 'grid',
    gridAutoFlow: 'column',
    justifyContent: 'start',
    alignItems: 'center',
    gridGap: 6,
    marginTop: 5,
  },
  pill: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridGap: 5,
    alignItems: 'center',
    height: 22,
    padding: '0 9px',
    borderRadius: 11,
    fontSize: '0.72rem',
    fontWeight: 800,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap',
    color: 'rgba(255, 255, 255, 0.85)',
    background: 'rgba(0, 0, 0, 0.45)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    '& svg': {
      fontSize: 13,
    },
  },
  hardPill: {
    color: '#fff',
    background: `linear-gradient(135deg, ${HARD}, #e64a19)`,
    border: '1px solid rgba(255, 171, 145, 0.5)',
    boxShadow: '0 0 12px rgba(255, 112, 67, 0.35)',
  },
  scroller: {
    overflowX: 'auto',
  },
  list: {
    minWidth: 720,
    padding: theme.spacing(1.25),
  },
  head: {
    display: 'grid',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(0.25, 1.5, 1),
    fontSize: '0.68rem',
    fontWeight: 800,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: PAGE_KIT.faint,
    whiteSpace: 'nowrap',
  },
  row: {
    display: 'grid',
    gridGap: theme.spacing(2),
    alignItems: 'center',
    minHeight: 52,
    padding: theme.spacing(0.75, 1.5),
    borderRadius: 12,
    cursor: 'pointer',
    background: 'rgba(255, 255, 255, 0.025)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    transition: 'background 0.15s, border-color 0.15s, transform 0.15s',
    '& + &': {
      marginTop: 6,
    },
    '&:hover': {
      background: 'rgba(66, 165, 245, 0.06)',
      borderColor: 'rgba(66, 165, 245, 0.35)',
      transform: 'translateY(-1px)',
    },
  },
  rank: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    fontWeight: 800,
    fontSize: '0.9rem',
    fontVariantNumeric: 'tabular-nums',
    color: PAGE_KIT.muted,
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  value: {
    fontSize: '1.05rem',
    fontWeight: 800,
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
  },
  num: {
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
    color: 'rgba(255, 255, 255, 0.8)',
  },
  group: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr)',
    gridGap: theme.spacing(1.25),
    alignItems: 'center',
  },
  classIcon: {
    display: 'block',
    width: 24,
    height: 24,
    borderRadius: '50%',
  },
  names: {
    fontSize: '0.85rem',
    color: PAGE_KIT.muted,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  player: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    justifyContent: 'start',
    gridGap: 10,
    alignItems: 'center',
    fontWeight: 700,
    minWidth: 0,
  },
  server: {
    display: 'inline-block',
    padding: '1px 8px',
    borderRadius: 9,
    fontSize: '0.72rem',
    fontWeight: 700,
    color: 'rgba(255, 255, 255, 0.8)',
    background: 'rgba(255, 255, 255, 0.07)',
  },
  date: {
    textAlign: 'right',
    fontSize: '0.85rem',
    color: PAGE_KIT.muted,
    whiteSpace: 'nowrap',
  },
}));

enum Board {
  Clear = "Clear",
  Kills = "Kills",
}

const Group: React.FC<{players: IPveLeaderboardPlayer[]}> = ({players}) => {
  const classes = useStyles();
  return (
    <span className={classes.group}>
      <ClassCounts players={players} />
      <span className={classes.names}>{players.map((p) => p.name).join(', ')}</span>
    </span>
  );
}

/** shown with "All runs": a row from a run that isn't verified (e.g. someone wasn't using the app) */
const Unverified: React.FC<{verified?: boolean}> = ({verified}) => verified === false ? (
  <Tooltip title="Unverified run: someone wasn't using the app, or the run wasn't cleared">
    <ErrorOutline style={{fontSize: 16, color: '#ffb74d', marginLeft: 6, verticalAlign: 'middle'}} />
  </Tooltip>
) : null;

/** The rank: a gold, silver or bronze badge for the top 3. */
const Rank: React.FC<{rank: number}> = ({rank}) => {
  const classes = useStyles();
  const medal = MEDALS[rank - 1];
  return (
    <span className={classes.rank} style={medal ? {background: medal.badge, color: medal.text, border: 0, boxShadow: `0 0 14px ${medal.color}55`} : undefined}>
      {rank}
    </span>
  );
}

/** a top 3 row is tinted in its medal's color */
const medalRow = (rank: number): React.CSSProperties | undefined => {
  const medal = MEDALS[rank - 1];
  return medal ? {background: `linear-gradient(90deg, ${medal.color}1c, rgba(255, 255, 255, 0.02) 50%)`, borderColor: `${medal.color}40`} : undefined;
}

/** Fastest clears and fastest boss kills. */
export const PveLeaderboard: React.FC = () => {
  const classes = useStyles();
  const page = usePageStyles();
  const history = useHistory();
  const ready = getReadyPveInstances();
  const [instanceId, setInstanceId] = useState<number>(ready[0]?.id || 0);
  // verified runs only by default; invalid runs (e.g. hard mode not met) are never on a leaderboard
  const [verifiedOnly, setVerifiedOnly] = useState(true);
  const [board, setBoard] = useState<Board>(Board.Clear);
  const [server, setServer] = useState<string>('');
  // the shared PvP / PvE seasons. null: the server's current season (runs are filed under it, even before it has an
  // entry in the season list); 0: all time
  const seasonsQuery = useQuery<LeaderboardSeasonsData>(LeaderboardSeasonsQuery);
  const seasons = seasonsQuery.data?.seasons || [];
  const [season, setSeason] = useState<number | null>(null);
  const instance = getPveInstance(instanceId);
  // a dungeon with a hard mode only ranks hard-mode runs (the others are invalid)
  const hardOnly = pveInstanceHasHardMode(instance);
  const encounters = instance?.encounters || [];
  const [encounter, setEncounter] = useState<string>(encounters[0]?.id || '');
  const [picking, setPicking] = useState(false);

  // a different dungeon has different bosses
  useEffect(() => {
    if(!encounters.some((e) => e.id == encounter))
      setEncounter(encounters[0]?.id || '');
  }, [instanceId]);

  const base = {instance: instanceId, verified: verifiedOnly, server: server || null, season};
  const clear = useQuery<PveClearLeaderboardData, PveClearLeaderboardVars>(PveClearLeaderboardQuery, {
    variables: base, skip: board != Board.Clear || !instanceId, fetchPolicy: 'cache-and-network',
  });
  const kills = useQuery<PveKillLeaderboardData, PveKillLeaderboardVars>(PveKillLeaderboardQuery, {
    variables: {...base, encounter}, skip: board != Board.Kills || !instanceId || !encounter, fetchPolicy: 'cache-and-network',
  });
  const loading = board == Board.Clear ? clear.loading : kills.loading;
  const rows = board == Board.Clear ? clear.data?.pveClearLeaderboard : kills.data?.pveKillLeaderboard;
  const open = (pve: string) => history.push(`/pve/run/${pve}`);

  const image = PveIcons[instanceId];
  const bossName = encounters.find((e) => e.id == encounter)?.name;
  const boardLabel = board == Board.Clear ? 'Fastest clears' : `Fastest kills${bossName ? ` · ${bossName}` : ''}`;
  // one template for the column labels and every row, so they line up
  const columns = '48px 104px minmax(0, 1fr) 80px 110px';

  return (
    <Grid container spacing={1} className="content-container">
      <Grid item xs={12}>
        <PveNav />
        <div className={clsx(page.panel, classes.filterPanel)}>
          <div className={classes.controls}>
            <EncounterPickerButton selectedId={instanceId || null} onClick={() => setPicking(true)} />
            <Segmented value={verifiedOnly} onChange={setVerifiedOnly} options={[
              {value: true, label: 'Verified only', icon: <VerifiedUser />},
              {value: false, label: 'All runs'},
            ]} />
            <Segmented value={server} onChange={setServer} options={[
              {value: '', label: 'All servers', icon: <Public />},
              {value: 'Fury', label: 'Fury'},
              {value: 'Crom', label: 'Crom'},
            ]} />
            <TextField select size="small" variant="outlined" label="Season" value={season != null ? season : 'current'} className={classes.select} style={{minWidth: 260}}
              onChange={(e) => setSeason(e.target.value == 'current' ? null : Number(e.target.value))}
              SelectProps={{renderValue: (selected: any) => {
                const found = seasons.find((s) => s.season == selected);
                return selected == 'current' ? 'Current season' : selected === 0 ? 'All Time' : found ? seasonLabel(found) : `Season ${selected}`;
              }}}>
              <MenuItem value="current">Current season</MenuItem>
              <MenuItem value={0}>All Time</MenuItem>
              {seasons.map((s) => <MenuItem key={s.season} value={s.season}>{seasonLabel(s)}</MenuItem>)}
            </TextField>
          </div>
          <div className={clsx(classes.controls, classes.controlsDivider)}>
            <Segmented value={board} onChange={setBoard} options={[
              {value: Board.Clear, label: 'Clear Times', icon: <Timer />},
              {value: Board.Kills, label: 'Boss Kills', icon: <Flag />},
            ]} />
            {board != Board.Clear &&
              <TextField select size="small" variant="outlined" label="Boss" value={encounter} className={classes.select} style={{minWidth: 220}}
                onChange={(e) => setEncounter(e.target.value as string)}>
                {encounters.map((e) => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
              </TextField>
            }
          </div>
        </div>
        <EncounterPicker open={picking} selectedId={instanceId || null} instances={ready}
          title="Leaderboard" subtitle="Pick a dungeon or raid to see its rankings."
          onClose={() => setPicking(false)} onSelect={(i) => { if(i) setInstanceId(i.id); setPicking(false); }} />
        <div className={page.tablePanel}>
          {loading && <LinearProgress style={{position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 1}} />}
          {instance &&
            <div className={classes.banner} style={{backgroundImage: `linear-gradient(90deg, rgba(18, 18, 22, 0.97) 30%, rgba(18, 18, 22, 0.7))${image ? `, url(${image})` : ''}`}}>
              <div className={classes.bannerIcon}><EmojiEvents /></div>
              <div style={{minWidth: 0}}>
                <div className={classes.bannerTitle}>{instance.name}</div>
                <div className={classes.bannerMeta}>
                  <span className={classes.pill}>{getPveKind(instance.id) == 'raid' ? 'Raid' : 'Dungeon'}</span>
                  {hardOnly && <Tooltip title={instance.hardModeActivation || 'Only hard-mode runs are ranked.'}><span className={clsx(classes.pill, classes.hardPill)}><Whatshot />Hard mode</span></Tooltip>}
                  <span className={classes.pill}>{verifiedOnly ? 'Verified runs' : 'All runs'}</span>
                  <span className={classes.pill}>{server || 'All servers'}</span>
                  <span className={classes.pill}>{season === 0 ? 'All Time' : season != null ? (seasons.find((s) => s.season == season)?.label || `Season ${season}`) : 'Current season'}</span>
                  <span className={page.muted} style={{marginLeft: 4}}>{boardLabel}</span>
                </div>
              </div>
            </div>
          }
          {(rows || []).length > 0 &&
            <div className={classes.scroller}>
              <div className={classes.list}>
                <div className={classes.head} style={{gridTemplateColumns: columns}}>
                  <span>#</span>
                  <span style={{textAlign: 'right'}}>{board == Board.Clear ? 'Clear Time' : 'Kill Time'}</span>
                  <span>Group</span>
                  <span>Server</span>
                  <span style={{textAlign: 'right'}}>Date</span>
                </div>
                {board == Board.Clear && (clear.data?.pveClearLeaderboard || []).map((row) => (
                  <div key={row.pve} className={classes.row} style={{gridTemplateColumns: columns, ...medalRow(row.rank)}} onClick={() => open(row.pve)}>
                    <Rank rank={row.rank} />
                    <span className={classes.value}>{formatPveDuration(row.clearTime)}</span>
                    <Group players={row.players} />
                    <span><span className={classes.server}>{row.server}</span><Unverified verified={row.verified} /></span>
                    <span className={classes.date}>{row.date ? moment(row.date).format('MMM D, YYYY') : ''}</span>
                  </div>
                ))}
                {board == Board.Kills && (kills.data?.pveKillLeaderboard || []).map((row) => (
                  <div key={row.pve} className={classes.row} style={{gridTemplateColumns: columns, ...medalRow(row.rank)}} onClick={() => open(row.pve)}>
                    <Rank rank={row.rank} />
                    <span className={classes.value}>{formatPveDuration(row.duration)}</span>
                    <Group players={row.players} />
                    <span><span className={classes.server}>{row.server}</span><Unverified verified={row.verified} /></span>
                    <span className={classes.date}>{row.date ? moment(row.date).format('MMM D, YYYY') : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          }
          {loading && !(rows || []).length && <Box p={4} textAlign="center"><CircularProgress /></Box>}
          {!loading && !(rows || []).length &&
            <PageEmpty icon={<EmojiEvents />} title={verifiedOnly ? 'No verified results yet' : 'No results yet'}
              text="Clear it or down a boss with the app running and your group could be the first on the board." />
          }
        </div>
      </Grid>
    </Grid>
  );
}
